import React from 'react';
import { Loader2, Printer, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const eur = (v) => `€${(v || 0).toFixed(2)}`;

const DiffBadge = ({ diff, testId }) => {
  const ok = Math.abs(diff) < 0.005;
  const cls = ok ? 'bg-green-100 text-green-700' : diff > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return (
    <span className={`px-2 py-1 rounded-md text-sm font-bold ${cls}`} data-testid={testId}>
      {ok ? 'TAM' : diff > 0 ? `FAZLA +${eur(diff)}` : `EKSİK -${eur(-diff)}`}
    </span>
  );
};

const CountRow = ({ label, expected, value, onChange, testId }) => (
  <div className="grid grid-cols-[1fr_auto] gap-2 items-center p-3 rounded-lg bg-stone-50 border">
    <div>
      <p className="text-sm font-semibold text-charcoal">{label}</p>
      <p className="text-xs text-charcoal/60">Beklenen: <b data-testid={`${testId}-expected`}>{eur(expected)}</b></p>
    </div>
    {value !== '' && <DiffBadge diff={(parseFloat(value) || 0) - expected} testId={`${testId}-diff`} />}
    <Input type="number" step="0.01" min="0" inputMode="decimal" placeholder="Sayılan tutar €" value={value}
      onChange={(e) => onChange(e.target.value)} className="col-span-2 text-lg font-semibold" data-testid={`${testId}-input`} />
  </div>
);

export const CashCountDialog = ({ eod, form, setForm, saving, onClose, onSave, onPrintOnly }) => (
  <Dialog open={!!eod} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-md" data-testid="cash-count-dialog">
      <DialogHeader>
        <DialogTitle className="font-playfair">Kasa Sayımı · {eod?.date?.split('-').reverse().join('.')}</DialogTitle>
      </DialogHeader>
      {eod && (
        <div className="space-y-3">
          {eod.cash_count && (
            <p className="text-xs text-charcoal/60" data-testid="cash-count-previous">
              Son kayıtlı sayım: {new Date(eod.cash_count.updated_at).toLocaleString('tr-TR')}{eod.cash_count.cashier ? ` · ${eod.cash_count.cashier}` : ''}
            </p>
          )}
          {eod.open_orders?.count > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-md p-2" data-testid="cash-count-open-orders">
              {eod.open_orders.count} açık/kapanmamış sipariş (€{eod.open_orders.amount.toFixed(2)}) beklenen kasaya dahil değil.
            </p>
          )}
          <CountRow label="Kasa Nakit (Salon/Masa + tahsilat)" expected={eod.cash_register.nakit_kasa} value={form.kasa} onChange={(v) => setForm(f => ({ ...f, kasa: v }))} testId="cash-count-kasa" />
          <CountRow label="Paket Nakit (Gel-Al + Online)" expected={eod.cash_register.nakit_paket} value={form.paket} onChange={(v) => setForm(f => ({ ...f, paket: v }))} testId="cash-count-paket" />
          {form.kasa !== '' && form.paket !== '' && (
            <div className="flex items-center justify-between px-3 text-sm" data-testid="cash-count-total-row">
              <span className="text-charcoal/70">Nakit toplam: <b>€{((parseFloat(form.kasa) || 0) + (parseFloat(form.paket) || 0)).toFixed(2)}</b> / beklenen €{eod.cash_register.nakit.toFixed(2)}</span>
              <DiffBadge diff={(parseFloat(form.kasa) || 0) + (parseFloat(form.paket) || 0) - eod.cash_register.nakit} testId="cash-count-total-diff" />
            </div>
          )}
          <CountRow label="Kart (POS gün sonu)" expected={eod.cash_register.kart} value={form.card} onChange={(v) => setForm(f => ({ ...f, card: v }))} testId="cash-count-card" />
          <Input placeholder="Kasiyer adı" value={form.cashier} onChange={(e) => setForm(f => ({ ...f, cashier: e.target.value }))} data-testid="cash-count-cashier" />
          <Input placeholder="Not (opsiyonel, ör. fark nedeni)" value={form.note} onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))} data-testid="cash-count-note" />
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onPrintOnly} className="flex-1" data-testid="cash-count-print-only">
              <Printer className="w-4 h-4 mr-1" />Sayımsız Yazdır
            </Button>
            <Button onClick={onSave} disabled={saving} className="flex-1 bg-basil text-white hover:bg-basil/90" data-testid="cash-count-save-print">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Kaydet ve Yazdır
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export default CashCountDialog;
